import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Input, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import fileDownload from 'js-file-download';
import DownloadIcon from 'mdi-react/DownloadIcon';
import Api from '../../../../util/api';
import { partial } from '../../../../util/translation/translation';
import { normalizeName } from '../../../../util/functions';
import userLanguageState from '../../../../recoil/userLanguage/selector';
import FormCard from '../../../../shared/components/custom/FormCard';
import Loading from '../../../../shared/components/custom/Loading';
import { formatDay, formatDayTime, getApiErrorMessage } from '../seriesUtils';

const fullName = (person) =>
  `${normalizeName(person.firstname)} ${normalizeName(person.lastname)}`.trim();

// Guests by name where they were given, else just how many
const guestsText = (person) => {
  const names = (person.guests || [])
    .map((guest) => guest?.name)
    .filter(Boolean);
  return names.length
    ? `${person.friendAmount} (${names.join(', ')})`
    : person.friendAmount || 0;
};

// Everyone booked on any occurrence of the series (each occurrence still has
// its own list, waitlist and attendance on its event page), and the
// whole-series bookings
const SeriesParticipants = ({ series }) => {
  const p = partial('EventSeries');
  const s = partial('shared');
  const userLanguage = useRecoilValue(userLanguageState);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await Api.series.participants(series.id, {
        from: from ? moment(from).startOf('day').toISOString() : undefined,
        to: to ? moment(to).endOf('day').toISOString() : undefined,
      });
      setParticipants(
        [...(data || [])].sort(
          (a, b) =>
            new Date(a.event?.startDate) - new Date(b.event?.startDate) ||
            fullName(a).localeCompare(fullName(b))
        )
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [series.id, from, to]);

  const downloadCsv = async () => {
    setLoading(true);
    try {
      const lang = ['sv', 'en'].includes(userLanguage) ? userLanguage : 'nb';
      const csv = await Api.series.participantsCsv(series.id, lang);
      const text = typeof csv === 'string' ? csv : String(csv ?? '');
      fileDownload(
        text.startsWith('﻿') ? text : `﻿${text}`,
        `${series.title} ${p('participantsFileName')}.csv`
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const seriesBookings = series.seriesBookings || [];

  return (
    <>
      <Loading loading={loading} />
      <p className="series-form__muted mb-3">{p('participantsHint')}</p>
      <FormCard
        title={
          <>
            {p('tabParticipants')}
            {participants.length > 0 && (
              <span className="form-card__count">{participants.length}</span>
            )}
          </>
        }
        actions={
          <div className="series-participants__filters">
            <Input
              type="date"
              bsSize="sm"
              value={from}
              aria-label={p('from')}
              title={p('from')}
              onChange={(event) => setFrom(event.target.value)}
            />
            <span className="text-muted">–</span>
            <Input
              type="date"
              bsSize="sm"
              value={to}
              aria-label={p('to')}
              title={p('to')}
              onChange={(event) => setTo(event.target.value)}
            />
            <Button
              size="sm"
              color="secondary"
              outline
              className="mb-0"
              onClick={downloadCsv}
            >
              <DownloadIcon size={14} /> {p('exportCsv')}
            </Button>
          </div>
        }
        bodyClassName="form-card__body--flush"
        className="mb-3"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{p('date')}</th>
              <th>{s('name')}</th>
              <th>{s('guests')}</th>
              <th>{p('booking')}</th>
              <th>{s('attended')}</th>
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted text-center py-4">
                  {p('noParticipants')}
                </td>
              </tr>
            )}
            {participants.map((participant) => {
              const details = [
                participant.phoneNumber,
                participant.email,
              ].filter(Boolean);
              return (
                <tr key={participant.id}>
                  <td data-label={p('date')}>
                    {participant.event ? (
                      <Link to={`/events/edit/${participant.event.id}?tab=3`}>
                        {formatDayTime(participant.event.startDate)}
                      </Link>
                    ) : (
                      '-'
                    )}
                    {participant.event?.cancelledAt && (
                      <Badge color="secondary" className="ml-2">
                        {p('statusCancelled')}
                      </Badge>
                    )}
                  </td>
                  <td className="table-stack__primary">
                    {fullName(participant)}
                    {details.length > 0 && (
                      <small className="text-muted d-block">
                        {details.join(' · ')}
                      </small>
                    )}
                  </td>
                  <td data-label={s('guests')}>{guestsText(participant)}</td>
                  <td data-label={p('booking')}>
                    {participant.seriesBookingId ? (
                      <Badge color="primary" pill>
                        {p('wholeSeries')}
                      </Badge>
                    ) : (
                      <span className="text-muted">{p('singleDate')}</span>
                    )}
                  </td>
                  <td data-label={s('attended')}>
                    {participant.attendedAmount || 0}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </FormCard>
      <FormCard
        title={
          <>
            {p('seriesBookings')}
            {seriesBookings.length > 0 && (
              <span className="form-card__count">{seriesBookings.length}</span>
            )}
          </>
        }
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{s('name')}</th>
              <th>{s('guests')}</th>
              <th>{p('bookedOn')}</th>
              <th>{s('comments')}</th>
            </tr>
          </thead>
          <tbody>
            {seriesBookings.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted text-center py-4">
                  {p('noSeriesBookings')}
                </td>
              </tr>
            )}
            {seriesBookings.map((booking) => {
              const details = [booking.phoneNumber, booking.email].filter(
                Boolean
              );
              return (
                <tr key={booking.id}>
                  <td className="table-stack__primary">
                    {fullName(booking)}
                    {details.length > 0 && (
                      <small className="text-muted d-block">
                        {details.join(' · ')}
                      </small>
                    )}
                  </td>
                  <td data-label={s('guests')}>{guestsText(booking)}</td>
                  <td data-label={p('bookedOn')}>
                    {formatDay(booking.createdAt)}
                  </td>
                  <td data-label={s('comments')}>
                    {booking.additionalNotes || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </FormCard>
    </>
  );
};

SeriesParticipants.propTypes = {
  series: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    seriesBookings: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default SeriesParticipants;
